import { useState } from 'react';
import { handleApiErrorToast } from '../../../../../../api/axios';
import { logger } from '../../../../../../services/logger';

/**
 * `failed`: the offering was not created, so it is safe to retry.
 * `unconfirmed`: adding it threw, possibly after the create had already
 * succeeded, so retrying could duplicate it.
 */
export type BulkOfferingAddStatus =
  | 'queued'
  | 'adding'
  | 'added'
  | 'failed'
  | 'unconfirmed';

/**
 * Adds bulk offerings one at a time (there is no bulk endpoint) and tracks each
 * offering's status for the progress list shown while the run is in flight.
 */
export const useBulkOfferingAddRun = () => {
  const [addStatuses, setAddStatuses] = useState<
    Record<string, BulkOfferingAddStatus>
  >({});
  // Offerings in the latest run, which for a retry is only the failed ones
  const [runSize, setRunSize] = useState(0);

  const setAddStatus = (offeringId: string, status: BulkOfferingAddStatus) => {
    setAddStatuses((prevStatuses) => ({
      ...prevStatuses,
      [offeringId]: status,
    }));
  };

  /**
   * Runs `addOffering` for each entry in order. A failure does not stop the
   * run. Entries left out keep their status, so a retry of the failed entries
   * keeps the ones already added.
   *
   * @returns the ids of the offerings that did not end up added
   */
  const runAdd = async <T extends { offeringId: string }>(
    entries: T[],
    addOffering: (entry: T) => Promise<boolean>,
  ): Promise<string[]> => {
    setRunSize(entries.length);
    setAddStatuses((prevStatuses) => ({
      ...prevStatuses,
      ...Object.fromEntries(
        entries.map(({ offeringId }) => [offeringId, 'queued' as const]),
      ),
    }));

    const notAddedOfferingIds: string[] = [];
    for (const entry of entries) {
      setAddStatus(entry.offeringId, 'adding');
      let status: BulkOfferingAddStatus;
      try {
        status = (await addOffering(entry)) ? 'added' : 'failed';
      } catch (error) {
        handleApiErrorToast(error);
        logger.error('ERROR bulk add offering', error);
        status = 'unconfirmed';
      }
      if (status !== 'added') {
        notAddedOfferingIds.push(entry.offeringId);
      }
      setAddStatus(entry.offeringId, status);
    }

    return notAddedOfferingIds;
  };

  const clearAddStatuses = () => setAddStatuses({});

  return { addStatuses, runSize, runAdd, clearAddStatuses };
};
